/**
 * The car's own search, checked for fit BEFORE it is split into pages
 * (P25009-492).
 *
 * P25009-486 made a car search show only the cards that pass the fit check,
 * but the check ran on the 24 cards of the page the API had returned. The
 * search itself does not filter on tyre size, so page 1 was mostly other
 * sizes: "0 resultat" for a Volvo EX30 with 14 fitting wheels on pages 2–5,
 * and a count that only described the page on screen.
 *
 * Here the whole candidate set is fetched with only the fields the fit check
 * reads (a few hundred listings is a handful of light requests), checked, and
 * only then paged. The page on screen is fetched in full by id, in the
 * candidates' order — which is the order the API sorted them in.
 *
 * What is shown, in order (the client's rule):
 *   1. listings in one of the car's own sizes (wheelFitLevel EXACT);
 *   2. none of those — listings in a close size (CLOSE);
 *   3. neither — the same again with one more of the car search's conditions
 *      given up (relaxationSteps: hub bore, then ET, then the clicked size),
 *      exactly like the unfiltered search widens when it finds nothing.
 */
import { sanitizeListing } from '../../util/sanitize';
import { relaxationSteps } from '../../util/carSearchParams';
import { FIT_LEVEL, wheelFitLevel } from '../../util/wheelFitment';
import { isCarPayload, isCarsOwnSearch, normalizeReg } from '../../util/savedCar';

export const FIT_CANDIDATE_PAGE_SIZE = 100;
/* 1,000 candidates. A car search with its diameters is far below that (a
 * Volvo XC60's winter tyres in 18–22": a few hundred); a query that is not
 * is checked on its first 1,000 in sort order and says so (`truncated`). */
export const FIT_CANDIDATE_MAX_PAGES = 10;
const PARALLEL_PAGES = 3;

/* What wheelFitLevel reads, and the position a distance chip trims on
 * (P25009-425). */
const FIT_FIELDS = [
  'geolocation',
  'publicData.numberOfBolts',
  'publicData.boltCircle',
  'publicData.hubHole',
  'publicData.diameter',
  'publicData.tireWidthFront',
  'publicData.profileFront',
  'publicData.adType',
  'publicData.listingType',
];

/* Params that shape what a listing looks like in the response, not which
 * listings match. */
export const isDisplayParam = key =>
  key === 'include' ||
  key.startsWith('fields.') ||
  key.startsWith('imageVariant.') ||
  key.startsWith('limit.');

/**
 * The car whose fit decides the result, or null when the page shows
 * everything. Same rule as the page's fit bar: the URL names the car's own
 * plate and "Visa även ej passande" (passar=0) is off, or passar=1 forces it.
 *
 * `storedCar` is this browser's remembered car (savedCar.readStoredCar): on a
 * reload the search runs before the car is back in Redux.
 *
 * A car chosen by model has no plate; the URL names it by `carId`, its
 * car-database id (P25009-491), and its search is checked the same way.
 */
export const fitSearchCar = ({ plateInfo, plateReg, storedCar, regNumber, carId, passar }) => {
  /* urlHelpers' parse() turns "passar=0" into the number 0. */
  const passarValue = passar == null ? null : String(passar);
  if (passarValue === '0') return null;
  const car = isCarPayload(plateInfo)
    ? { data: plateInfo, reg: normalizeReg(plateReg) }
    : storedCar && isCarPayload(storedCar.data)
      ? { data: storedCar.data, reg: normalizeReg(storedCar.reg) }
      : null;
  if (!car) return null;
  const search = new URLSearchParams();
  if (regNumber != null && regNumber !== '') search.set('regNumber', String(regNumber));
  if (carId != null && carId !== '') search.set('carId', String(carId));
  const isOwnSearch = isCarsOwnSearch(search, car);
  return isOwnSearch || passarValue === '1' ? car.data : null;
};

/* A sort with ties pages unstably: most listings share meta_boostStartDate and
 * a weekly cron rewrites pub_updateDate, so page 2 repeats some of page 1 and
 * skips others (Live, an XC60's wheels: 137 rows fetched, 129 distinct).
 * createdAt breaks the tie. The API takes three sort keys at most. */
export const stableSort = sort => {
  if (!sort) return sort;
  const keys = String(sort).split(',');
  const hasCreatedAt = keys.some(k => k.replace(/^-/, '') === 'createdAt');
  return hasCreatedAt || keys.length >= 3 ? sort : [...keys, 'createdAt'].join(',');
};

export const fetchCandidates = async (sdk, query) => {
  const pageQuery = page => sdk.listings.query({ ...query, perPage: FIT_CANDIDATE_PAGE_SIZE, page });
  const first = await pageQuery(1);
  const totalPages = first.data.meta?.totalPages || 1;
  const lastPage = Math.min(totalPages, FIT_CANDIDATE_MAX_PAGES);
  const responses = [first];
  for (let page = 2; page <= lastPage; page += PARALLEL_PAGES) {
    const batch = [];
    for (let p = page; p < page + PARALLEL_PAGES && p <= lastPage; p += 1) batch.push(pageQuery(p));
    responses.push(...(await Promise.all(batch)));
  }
  /* And should a tie still slip through, a listing counts once. */
  const seen = new Set();
  const listings = responses
    .flatMap(r => r.data.data || [])
    .filter(l => !seen.has(l.id.uuid) && seen.add(l.id.uuid));
  return { listings, truncated: totalPages > FIT_CANDIDATE_MAX_PAGES };
};

/**
 * Run a car search and return one page of the listings that fit.
 *
 * @param {Object} options
 * @param {Object} options.sdk     Sharetribe SDK
 * @param {Object} options.params  the search's query params (filters + display)
 * @param {Object} options.car     the plate payload to check against
 * @param {number} options.page    1-based page of the FITTING listings
 * @param {number} options.perPage
 * @param {Array}  options.listingFields  hosted listing fields, so the check
 *   reads values exactly as the cards will (sanitizeListing nulls a value that
 *   is not a Console option)
 * @param {Function} [options.keep] a further test every candidate must pass
 *   before the fit check — the distance chip's circle (P25009-425)
 * @returns {Promise<{ response, relaxed: string[], fit: Object }>} `response`
 *   looks like an SDK listings.query response for that page.
 */
export const queryFitResults = async ({
  sdk,
  params,
  car,
  page = 1,
  perPage,
  listingFields,
  keep = null,
}) => {
  const filters = Object.fromEntries(Object.entries(params).filter(([k]) => !isDisplayParam(k)));
  const display = Object.fromEntries(Object.entries(params).filter(([k]) => isDisplayParam(k)));
  delete filters.page;
  delete filters.perPage;
  if (filters.sort) filters.sort = stableSort(filters.sort);

  const levelOf = listing =>
    wheelFitLevel({
      listingPublicData: sanitizeListing(listing, { listingFields }).attributes?.publicData || {},
      licensePlateInfo: car,
    });

  /* [] (as asked), then each relaxation step on top of the ones before. */
  const levels = relaxationSteps(filters).reduce(
    (acc, step) => [...acc, [...acc[acc.length - 1], ...step]],
    [[]]
  );

  let chosen = null;
  let checked = null;
  let truncated = false;
  for (const relaxed of levels) {
    const query = { ...filters, 'fields.listing': FIT_FIELDS };
    relaxed.forEach(key => delete query[key]);
    const candidates = await fetchCandidates(sdk, query);
    if (checked === null) {
      checked = candidates.listings.length;
      truncated = candidates.truncated;
    }
    const byLevel = { [FIT_LEVEL.EXACT]: [], [FIT_LEVEL.CLOSE]: [] };
    const kept = keep ? candidates.listings.filter(keep) : candidates.listings;
    kept.forEach(l => byLevel[levelOf(l)]?.push(l.id));
    const level = byLevel[FIT_LEVEL.EXACT].length > 0 ? FIT_LEVEL.EXACT : FIT_LEVEL.CLOSE;
    if (byLevel[level].length > 0) {
      chosen = { ids: byLevel[level], level, relaxed };
      break;
    }
  }

  const ids = chosen ? chosen.ids : [];
  const size = perPage || ids.length || 1;
  const pageIds = ids.slice((page - 1) * size, page * size);

  let data = [];
  let included = [];
  if (pageIds.length > 0) {
    const full = await sdk.listings.query({ ...display, ids: pageIds, perPage: pageIds.length });
    const byId = new Map((full.data.data || []).map(l => [l.id.uuid, l]));
    data = pageIds.map(id => byId.get(id.uuid)).filter(Boolean);
    included = full.data.included || [];
  }

  return {
    response: {
      data: {
        data,
        included,
        meta: {
          totalItems: ids.length,
          totalPages: Math.max(1, Math.ceil(ids.length / size)),
          page,
          perPage: size,
        },
      },
    },
    relaxed: chosen ? chosen.relaxed : [],
    fit: { applied: true, level: chosen ? chosen.level : null, checked: checked || 0, truncated },
  };
};
